import { supabase } from '@/lib/supabaseClient';

// Global cache for tenant ID to prevent repeated database calls
const tenantIdCache = new Map<string, {
  tenantId: string | null;
  timestamp: number;
  promise?: Promise<string | null>;
}>();

// Cache expiration time (10 minutes)
const TENANT_ID_CACHE_EXPIRY = 10 * 60 * 1000;

// Deduplicated tenant ID request function
const deduplicatedTenantIdRequest = async (
  cacheKey: string,
  requestFn: () => Promise<string | null>
): Promise<string | null> => {
  const cached = tenantIdCache.get(cacheKey);
  const now = Date.now();

  // If there's an ongoing request, wait for it (priority over cached data)
  if (cached?.promise) {
    return await cached.promise;
  }

  // Return cached data if it's still valid and not null
  if (cached && now - cached.timestamp < TENANT_ID_CACHE_EXPIRY && cached.tenantId !== null) {
    return cached.tenantId;
  }

  // Make the request and cache the promise
  const promise = requestFn();
  tenantIdCache.set(cacheKey, {
    tenantId: null,
    timestamp: now,
    promise
  });

  try {
    const result = await promise;
    tenantIdCache.set(cacheKey, {
      tenantId: result,
      timestamp: now
    });
    return result;
  } catch (error) {
    // Remove failed request from cache
    tenantIdCache.delete(cacheKey);
    throw error;
  }
};

/**
 * Get the current tenant ID for the authenticated user with caching
 * @returns Promise<string | null> - The current tenant ID
 * @throws Error if no tenant ID is available or user is not authenticated
 */
export const getCurrentTenantId = async (): Promise<string | null> => {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const cacheKey = `tenant-id-${user.id}`;

    const tenantId = await deduplicatedTenantIdRequest(cacheKey, async () => {
      const { data: tenantId, error } = await supabase
        .rpc('get_current_user_tenant_id');

      if (error) {
        console.error('[tenantUtils] Error getting current tenant ID:', error);
        throw error;
      }

      // Check for null, undefined, or the string "null"
      if (!tenantId || tenantId === 'null') {
        console.error('[tenantUtils] RPC returned invalid tenant ID:', tenantId);
        // Clear the cache since we got an invalid result
        tenantIdCache.delete(cacheKey);
        throw new Error('No tenant ID available');
      }

      return tenantId;
    });

    return tenantId;
  } catch (error) {
    console.error('[tenantUtils] Error in getCurrentTenantId:', error);
    throw error;
  }
};

/**
 * Get the current tenant ID with fallback to default
 * @returns Promise<string> - The current tenant ID or default tenant ID
 */
export const getCurrentTenantIdWithFallback = async (): Promise<string> => {
  try {
    const tenantId = await getCurrentTenantId();
    if (!tenantId) {
      throw new Error('No tenant ID available');
    }
    return tenantId;
  } catch (error) {
    // Silently fall back to default tenant (reduce console noise)
    // console.warn('[tenantUtils] Falling back to Joules tenant ID:', error);
    return '511f29e8-bef3-4065-9927-777d6eafe7f1';
  }
};

/**
 * Clear the tenant ID cache for a specific user or all users
 * @param userId - Optional user ID to clear cache for specific user
 */
export const clearTenantIdCache = (userId?: string) => {
  if (userId) {
    tenantIdCache.delete(`tenant-id-${userId}`);
  } else {
    tenantIdCache.clear();
  }
};

/**
 * Get cache statistics for tenant ID cache
 * @returns Object with cache statistics
 */
export const getTenantIdCacheStats = () => {
  const now = Date.now();
  const entries = Array.from(tenantIdCache.entries());
  const validEntries = entries.filter(([_, value]) => now - value.timestamp < TENANT_ID_CACHE_EXPIRY);
  
  return {
    totalEntries: entries.length,
    validEntries: validEntries.length,
    expiredEntries: entries.length - validEntries.length
  };
};
